import { getEffectiveFormat, isRuntimeFormat } from "../formats/formatsStore";
import { DocumentOrientation } from "../drawer";
import { extractIDsFromURL, getStoredOrientation } from "./orientationStorage";

/**
 * Calculates PDF dimensions in millimeters from a format string
 * @param format - The format string (e.g., "A5", "POSTCARD_9x6")
 * @returns Object with width and height in millimeters, or null if format not found
 */
export function getFormatDimensionsInMm(format: string): { width: number; height: number } | null {
  const formatConfig = getEffectiveFormat(format);

  if (!formatConfig) {
    console.warn(`Format ${format} not found in the format catalogue`);
    return null;
  }

  // Check if format has physical dimensions in millimeters
  if (formatConfig.widthInMm && formatConfig.heightInMm) {
    console.log(`📐 Format ${format}: ${formatConfig.widthInMm}mm x ${formatConfig.heightInMm}mm`);

    return {
      width: formatConfig.widthInMm,
      height: formatConfig.heightInMm,
    };
  }

  console.warn(`Could not determine dimensions for format: ${format}`);
  return null;
}

/**
 * Gets standard format dimensions in points for backward compatibility
 * This matches the server-side FORMAT_DIMENSIONS constants
 */
export function getFormatDimensionsInPoints(format: string): { width: number; height: number } | null {
  const mmDimensions = getFormatDimensionsInMm(format);
  if (!mmDimensions) return null;

  // Convert mm to points (1 mm = 2.834645669 points)
  return {
    width: Math.round(mmDimensions.width * 2.834645669),
    height: Math.round(mmDimensions.height * 2.834645669),
  };
}

/**
 * Format dimension mappings for quick reference in millimeters
 * These are commonly used formats with their exact dimensions
 */
export const COMMON_FORMAT_DIMENSIONS_MM = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  A6: { width: 105, height: 148 },
  DL: { width: 99, height: 210 },
  DOOR_HANGER: { width: 95, height: 222 },
  TEARAWAY: { width: 85, height: 150 },
  SMALL: { width: 294.64, height: 107.95 },
  BEST_SELLER: { width: 228.6, height: 158.75 },
  LARGE: { width: 279.4, height: 158.75 },
  JUMBO: { width: 279.4, height: 215.9 },
  OVERSIZED: { width: 381, height: 304.8 },
  POSTCARD_6x4: { width: 152.4, height: 101.6 }, // 6" x 4"
  POSTCARD_9x6: { width: 228.6, height: 152.4 }, // 9" x 6"
  POSTCARD_11x6: { width: 279.4, height: 152.4 }, // 11" x 6"
  POSTCARD_15x12: { width: 381, height: 304.8 }, // 15" x 12"
} as const;

/**
 * Gets dimensions in millimeters with fallback to common formats
 * @param format - Format string
 * @returns Dimensions in millimeters or A5 as fallback
 */
export function getFormatDimensionsWithFallback(format: string): { width: number; height: number } {
  // Try to get from FORMATS constants first
  const dimensions = getFormatDimensionsInMm(format);
  if (dimensions) return dimensions;

  // Try common format dimensions
  const commonFormat = COMMON_FORMAT_DIMENSIONS_MM[format as keyof typeof COMMON_FORMAT_DIMENSIONS_MM];
  if (commonFormat) {
    console.log(`📐 Using common format dimensions for ${format}: ${commonFormat.width}mm x ${commonFormat.height}mm`);
    return commonFormat;
  }

  // Fallback to A5
  console.warn(`Using A5 fallback dimensions for unknown format: ${format}`);
  return COMMON_FORMAT_DIMENSIONS_MM.A5;
}

/**
 * Gets dimensions in millimeters with orientation support
 * @param format - Format string
 * @returns Dimensions in millimeters considering orientation
 */
export function getFormatDimensionsWithOrientation(
  format: string,
  designId?: string,
  campaignId?: string
): { width: number; height: number } {
  // Get base dimensions
  const baseDimensions = getFormatDimensionsWithFallback(format);

  // Get stored orientation from localStorage using the new pattern with design/campaign ID
  const config = getEffectiveFormat(format);
  const storedOrientation = getStoredOrientation(
    designId,
    campaignId,
    config?.defaultOrientation ?? DocumentOrientation.Portrait
  );
  const isPortrait = storedOrientation === "Portrait";

  // Some formats don't support landscape (TEARAWAY, DOOR_HANGER, or whatever the catalogue says)
  const isLandscapeEnabled = isRuntimeFormat(config)
    ? config.allowLandscape
    : format !== "TEARAWAY" && format !== "DOOR_HANGER";

  // Determine the natural orientation of the base dimensions
  const isNaturallyLandscape = baseDimensions.width > baseDimensions.height;

  // Return dimensions based on orientation
  if (isPortrait) {
    if (isNaturallyLandscape) {
      // Base is landscape but user wants portrait — swap to make height > width
      const portraitDimensions = {
        width: baseDimensions.height,
        height: baseDimensions.width,
      };
      console.log(
        `📐 ${format} in Portrait (swapped from landscape base): ${portraitDimensions.width}mm x ${portraitDimensions.height}mm`
      );
      return portraitDimensions;
    }
    // Base is already portrait
    console.log(`📐 ${format} in Portrait: ${baseDimensions.width}mm x ${baseDimensions.height}mm`);
    return baseDimensions;
  } else if (!isLandscapeEnabled) {
    // Landscape disabled for this format — use base as-is
    console.log(`📐 ${format} (landscape disabled): ${baseDimensions.width}mm x ${baseDimensions.height}mm`);
    return baseDimensions;
  } else {
    // Landscape
    if (!isNaturallyLandscape) {
      // Base is portrait but user wants landscape — swap to make width > height
      const landscapeDimensions = {
        width: baseDimensions.height,
        height: baseDimensions.width,
      };
      console.log(
        `📐 ${format} in Landscape (swapped from portrait base): ${landscapeDimensions.width}mm x ${landscapeDimensions.height}mm`
      );
      return landscapeDimensions;
    }
    // Base is already landscape
    console.log(`📐 ${format} in Landscape: ${baseDimensions.width}mm x ${baseDimensions.height}mm`);
    return baseDimensions;
  }
}
